/**
 * Al Muslim Books reader chrome for Why Islam modules.
 * Markup and behavior mirror `assets/books/src/index.ts` (reader header, tools, TOC dropdown, settings sheet).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ayahLineChoiceId,
  ayahLineChoices,
  booksReaderArticleStyle,
  booksReaderContentClassNames,
  booksReaderCssVariables,
  booksReaderHostClassNames,
  defaultBooksReaderSettings,
  lineHeightChoices,
  lineHeightChoiceId,
  loadBooksReaderSettings,
  saveBooksReaderSettings,
  type BooksReaderSettings,
} from './booksReaderPrefs';
import './books-reader-module.css';

export type ModuleReaderPanel = 'toc' | 'note' | 'more' | 'reader' | null;

type TabId = 'typography' | 'layout' | 'display' | 'quran' | 'reading';

type Props = {
  title: string;
  estimatedMinutes?: number | null;
  scrollPct: number;
  backHref: string;
  bookmarked: boolean;
  bookmarkBusy: boolean;
  bookmarkAck?: string | null;
  bookmarkErr?: string | null;
  onSaveBookmark: () => void;
  toc: React.ReactNode;
  notePanel: React.ReactNode;
  morePanel: React.ReactNode;
  /** Appended to `.books-reader-content` (e.g. study depth). */
  contentExtraClass?: string;
  children: React.ReactNode;
};

export function BooksReaderModuleShell({
  title,
  estimatedMinutes,
  scrollPct,
  backHref,
  bookmarked,
  bookmarkBusy,
  bookmarkAck,
  bookmarkErr,
  onSaveBookmark,
  toc,
  notePanel,
  morePanel,
  contentExtraClass,
  children,
}: Props) {
  const [settings, setSettings] = useState<BooksReaderSettings>(() => loadBooksReaderSettings());
  const [openPanel, setOpenPanel] = useState<ModuleReaderPanel>(null);
  const [readerTab, setReaderTab] = useState<TabId>('typography');

  useEffect(() => {
    saveBooksReaderSettings(settings);
  }, [settings]);

  useEffect(() => {
    // Only lock background scroll for full-screen overlays.
    // TOC and Notes should not "blank" the reading surface.
    if (openPanel !== 'reader' && openPanel !== 'more') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openPanel]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenPanel(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hostStyle = useMemo(() => booksReaderCssVariables(settings), [settings]);
  const hostClass = useMemo(() => booksReaderHostClassNames(settings), [settings]);
  const contentStyle = useMemo(() => booksReaderArticleStyle(settings), [settings]);
  const contentClass = useMemo(() => {
    const base = booksReaderContentClassNames(settings);
    return contentExtraClass ? `${base} ${contentExtraClass}`.trim() : base;
  }, [settings, contentExtraClass]);

  const fontPct = Math.round(settings.readerSize * 100);

  const bumpSize = (delta: number) => {
    setSettings((s) => ({
      ...s,
      readerSize: Math.min(1.45, Math.max(0.7, s.readerSize + delta)),
    }));
  };

  const setLineHeight = (value: number) => {
    setSettings((s) => ({ ...s, lineHeight: value }));
  };

  const setAyahLine = (value: number) => {
    setSettings((s) => ({ ...s, ayahLineHeight: value }));
  };

  const resetDefaults = () => {
    setSettings(defaultBooksReaderSettings());
  };

  const togglePanel = useCallback((p: Exclude<ModuleReaderPanel, null>) => {
    setOpenPanel((x) => (x === p ? null : p));
  }, []);

  const closePanels = useCallback(() => setOpenPanel(null), []);

  const lineActive = lineHeightChoiceId(settings.lineHeight);
  const ayahActive = ayahLineChoiceId(settings.ayahLineHeight);

  return (
    <div className={hostClass} style={hostStyle}>
      <div
        className="books-reader-progress-wrap books-reader-progress-sticky"
        id="wi-books-reader-progress-wrap"
        role="presentation"
        hidden={!settings.showProgress}
      >
        <div
          className="books-reader-progress"
          style={{ width: `${scrollPct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(scrollPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <header className="books-reader-header">
        <div className="books-reader-header-row books-reader-header-top">
          <Link to={backHref} className="books-reader-back" aria-label="Back">
            ←
          </Link>
          <h1 className="books-reader-title">{title}</h1>
          <span className="books-reader-time-left" aria-live="polite">
            {estimatedMinutes != null && estimatedMinutes > 0 ? `~${estimatedMinutes} min` : ''}
          </span>
          <button
            type="button"
            className="books-reader-menu-btn"
            aria-label="Reader settings"
            onClick={() => togglePanel('reader')}
          >
            ⚙
          </button>
        </div>
        <div className="books-reader-header-tools" aria-label="Reader tools">
          <button
            type="button"
            className={`books-reader-bookmark${bookmarked ? ' is-active' : ''}`}
            aria-label="Save module to study"
            disabled={bookmarkBusy || bookmarked}
            onClick={() => {
              closePanels();
              onSaveBookmark();
            }}
          >
            {bookmarked ? 'Saved' : bookmarkBusy ? 'Saving…' : 'Save to study'}
          </button>
          {bookmarkErr ? <span className="books-reader-inline-msg books-reader-inline-msg--err">{bookmarkErr}</span> : null}
          {bookmarkAck ? <span className="books-reader-inline-msg">{bookmarkAck}</span> : null}
          <button
            type="button"
            className="books-reader-annotations"
            aria-label="Private note"
            aria-expanded={openPanel === 'note'}
            onClick={() => togglePanel('note')}
          >
            Notes
          </button>
          <button
            type="button"
            className="books-reader-goto-bookmark"
            aria-expanded={openPanel === 'more'}
            onClick={() => togglePanel('more')}
          >
            Deeper thoughts
          </button>
          <div className="books-reader-toc-wrap">
            <button
              type="button"
              className="books-reader-toc-btn"
              id="wi-books-reader-toc-btn"
              aria-expanded={openPanel === 'toc'}
              aria-controls="wi-books-reader-toc"
              onClick={() => togglePanel('toc')}
            >
              Chapters
            </button>
            <nav
              id="wi-books-reader-toc"
              className="books-reader-toc"
              aria-label="Table of contents"
              hidden={openPanel !== 'toc'}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a[href^="#"]')) closePanels();
              }}
            >
              {toc}
            </nav>
          </div>
        </div>
      </header>

      {openPanel === 'reader' ? (
        <div className="books-reader-settings-page" aria-label="Reader settings">
          <div className="books-reader-settings-inner">
            <div className="books-reader-settings-head">
              <h2 className="books-reader-settings-title">Reader settings</h2>
              <p className="books-reader-settings-sub">Fine typography, layout, display, Quran, and reading mode—organized by tab.</p>
            </div>
            <div className="books-reader-settings-tabs" role="tablist" aria-label="Settings sections">
              {(
                [
                  ['typography', 'Text'],
                  ['layout', 'Layout'],
                  ['display', 'Display'],
                  ['quran', 'Quran'],
                  ['reading', 'Reading'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  className={`books-reader-settings-tab${readerTab === id ? ' is-active' : ''}`}
                  aria-selected={readerTab === id}
                  onClick={() => setReaderTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            {readerTab === 'typography' ? (
              <div role="tabpanel" className="books-reader-settings-panel">
                <div className="books-reader-settings-section" aria-label="Font size">
                  <label className="books-reader-settings-label">
                    Font size <span className="books-reader-settings-value">{fontPct}%</span>
                  </label>
                  <div className="books-reader-font-row">
                    <div className="books-reader-font-wrap">
                      <button type="button" className="books-reader-font-btn" aria-label="Smaller text" onClick={() => bumpSize(-0.1)}>
                        A−
                      </button>
                      <button type="button" className="books-reader-font-btn" aria-label="Larger text" onClick={() => bumpSize(0.1)}>
                        A+
                      </button>
                    </div>
                  </div>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-font-family">
                    Font
                  </label>
                  <select
                    id="wi-books-font-family"
                    className="books-reader-select"
                    value={settings.font}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, font: e.target.value as BooksReaderSettings['font'] }))
                    }
                  >
                    <option value="serif">Serif</option>
                    <option value="sans">Sans</option>
                    <option value="dyslexia">Dyslexia-friendly</option>
                    <option value="system">System</option>
                  </select>
                </div>
                <div className="books-reader-settings-section" aria-label="Line spacing">
                  <label className="books-reader-settings-label">Line spacing</label>
                  <div className="books-reader-line-wrap books-reader-line-wrap--scroll">
                    {lineHeightChoices.map(({ id, value }) => (
                      <button
                        key={id}
                        type="button"
                        className={`books-reader-line-btn${lineActive === id ? ' is-active' : ''}`}
                        onClick={() => setLineHeight(value)}
                      >
                        {id.charAt(0).toUpperCase() + id.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-block-spacing">
                    Block spacing
                  </label>
                  <select
                    id="wi-books-block-spacing"
                    className="books-reader-select"
                    value={settings.blockSpacing}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        blockSpacing: e.target.value as BooksReaderSettings['blockSpacing'],
                      }))
                    }
                  >
                    <option value="compact">Compact</option>
                    <option value="normal">Normal</option>
                    <option value="spaced">Spaced</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-letter-spacing">
                    Letter spacing
                  </label>
                  <select
                    id="wi-books-letter-spacing"
                    className="books-reader-select"
                    value={settings.letterSpacing}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        letterSpacing: e.target.value as BooksReaderSettings['letterSpacing'],
                      }))
                    }
                  >
                    <option value="tight">Tight</option>
                    <option value="normal">Normal</option>
                    <option value="relaxed">Relaxed</option>
                    <option value="wide">Wide</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-word-spacing">
                    Word spacing
                  </label>
                  <select
                    id="wi-books-word-spacing"
                    className="books-reader-select"
                    value={settings.wordSpacing}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        wordSpacing: e.target.value as BooksReaderSettings['wordSpacing'],
                      }))
                    }
                  >
                    <option value="normal">Normal</option>
                    <option value="wide">Wide</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-paragraph-gap">
                    Paragraph spacing
                  </label>
                  <select
                    id="wi-books-paragraph-gap"
                    className="books-reader-select"
                    value={settings.paragraphGap}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        paragraphGap: e.target.value as BooksReaderSettings['paragraphGap'],
                      }))
                    }
                  >
                    <option value="compact">Compact</option>
                    <option value="normal">Normal</option>
                    <option value="relaxed">Relaxed</option>
                    <option value="airy">Airy</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-body-weight">
                    Body weight
                  </label>
                  <select
                    id="wi-books-body-weight"
                    className="books-reader-select"
                    value={settings.bodyWeight}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        bodyWeight: e.target.value as BooksReaderSettings['bodyWeight'],
                      }))
                    }
                  >
                    <option value="normal">Regular</option>
                    <option value="medium">Medium</option>
                    <option value="semibold">Semibold</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-hyphens">
                    Hyphenation
                  </label>
                  <select
                    id="wi-books-hyphens"
                    className="books-reader-select"
                    value={settings.hyphens}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        hyphens: e.target.value as BooksReaderSettings['hyphens'],
                      }))
                    }
                  >
                    <option value="off">Off</option>
                    <option value="auto">Automatic</option>
                  </select>
                </div>
                <div className="books-reader-settings-section books-reader-settings-check">
                  <label className="books-reader-settings-label books-reader-settings-check-label">
                    <input
                      type="checkbox"
                      checked={settings.firstLineIndent}
                      onChange={(e) => setSettings((s) => ({ ...s, firstLineIndent: e.target.checked }))}
                    />
                    Indent first line of paragraphs
                  </label>
                  <p className="books-reader-settings-micro">Classic book style: indent after each paragraph break.</p>
                </div>
              </div>
            ) : null}

            {readerTab === 'layout' ? (
              <div role="tabpanel" className="books-reader-settings-panel">
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-margin">
                    Line width (margins)
                  </label>
                  <select
                    id="wi-books-margin"
                    className="books-reader-select"
                    value={settings.margin}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, margin: e.target.value as BooksReaderSettings['margin'] }))
                    }
                  >
                    <option value="narrow">Narrow column</option>
                    <option value="normal">Balanced</option>
                    <option value="wide">Wide</option>
                    <option value="extra">Extra wide</option>
                    <option value="full">Full width</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-theme">
                    Theme
                  </label>
                  <select
                    id="wi-books-theme"
                    className="books-reader-select"
                    value={settings.theme}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, theme: e.target.value as BooksReaderSettings['theme'] }))
                    }
                  >
                    <option value="light">Light</option>
                    <option value="sepia">Sepia</option>
                    <option value="dark">Dark</option>
                    <option value="contrast">High contrast</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-justify">
                    Text alignment
                  </label>
                  <select
                    id="wi-books-justify"
                    className="books-reader-select"
                    value={settings.justify}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        justify: e.target.value as BooksReaderSettings['justify'],
                      }))
                    }
                  >
                    <option value="left">Left</option>
                    <option value="center">Centre</option>
                    <option value="right">Right</option>
                    <option value="justify">Justified</option>
                  </select>
                </div>
                <div className="books-reader-settings-section" aria-label="Verse block line spacing">
                  <label className="books-reader-settings-label">Verse block line spacing</label>
                  <div className="books-reader-line-wrap books-reader-line-wrap--scroll">
                    {ayahLineChoices.map(({ id, value }) => (
                      <button
                        key={id}
                        type="button"
                        className={`books-reader-line-btn${ayahActive === id ? ' is-active' : ''}`}
                        onClick={() => setAyahLine(value)}
                      >
                        {id.charAt(0).toUpperCase() + id.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-ayah-align">
                    Verse block alignment
                  </label>
                  <select
                    id="wi-books-ayah-align"
                    className="books-reader-select"
                    value={settings.ayahAlign}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        ayahAlign: e.target.value as BooksReaderSettings['ayahAlign'],
                      }))
                    }
                  >
                    <option value="left">Left</option>
                    <option value="center">Centre</option>
                    <option value="right">Right</option>
                    <option value="justify">Justified</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-heading-scale">
                    Heading size
                  </label>
                  <select
                    id="wi-books-heading-scale"
                    className="books-reader-select"
                    value={settings.headingScale}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        headingScale: e.target.value as BooksReaderSettings['headingScale'],
                      }))
                    }
                  >
                    <option value="compact">Compact</option>
                    <option value="normal">Balanced</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-arabic-scale">
                    Arabic script size
                  </label>
                  <select
                    id="wi-books-arabic-scale"
                    className="books-reader-select"
                    value={settings.arabicScale}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        arabicScale: e.target.value as BooksReaderSettings['arabicScale'],
                      }))
                    }
                  >
                    <option value="small">Smaller</option>
                    <option value="normal">Balanced</option>
                    <option value="large">Larger</option>
                    <option value="xlarge">Largest</option>
                  </select>
                </div>
              </div>
            ) : null}

            {readerTab === 'display' ? (
              <div role="tabpanel" className="books-reader-settings-panel">
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-highlight-style">
                    Highlight colour
                  </label>
                  <select
                    id="wi-books-highlight-style"
                    className="books-reader-select"
                    value={settings.highlightStyle}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        highlightStyle: e.target.value as BooksReaderSettings['highlightStyle'],
                      }))
                    }
                  >
                    <option value="yellow">Gold</option>
                    <option value="amber">Amber</option>
                    <option value="green">Green</option>
                    <option value="teal">Teal</option>
                    <option value="rose">Rose</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-link-style">
                    Links
                  </label>
                  <select
                    id="wi-books-link-style"
                    className="books-reader-select"
                    value={settings.linkStyle}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        linkStyle: e.target.value as BooksReaderSettings['linkStyle'],
                      }))
                    }
                  >
                    <option value="default">Theme default</option>
                    <option value="always">Always underline</option>
                  </select>
                </div>
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-image-style">
                    Images
                  </label>
                  <select
                    id="wi-books-image-style"
                    className="books-reader-select"
                    value={settings.imageStyle}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        imageStyle: e.target.value as BooksReaderSettings['imageStyle'],
                      }))
                    }
                  >
                    <option value="rounded">Rounded</option>
                    <option value="soft">Soft corners</option>
                    <option value="sharp">Square</option>
                  </select>
                </div>
                <div className="books-reader-settings-section books-reader-settings-check">
                  <label className="books-reader-settings-label books-reader-settings-check-label">
                    <input
                      type="checkbox"
                      checked={settings.showProgress}
                      onChange={(e) => setSettings((s) => ({ ...s, showProgress: e.target.checked }))}
                    />
                    Show progress bar at top
                  </label>
                </div>
                <div className="books-reader-settings-section books-reader-settings-check">
                  <label className="books-reader-settings-label books-reader-settings-check-label">
                    <input
                      type="checkbox"
                      checked={settings.reduceMotion}
                      onChange={(e) => setSettings((s) => ({ ...s, reduceMotion: e.target.checked }))}
                    />
                    Reduce motion &amp; transitions
                  </label>
                  <p className="books-reader-settings-micro">Adds stricter limits on animations (in addition to your system preference).</p>
                </div>
                <div className="books-reader-settings-section">
                  <button type="button" className="books-reader-settings-reset" onClick={resetDefaults}>
                    Reset reader to defaults
                  </button>
                  <p className="books-reader-settings-micro">Clears saved typography and display choices for this browser.</p>
                </div>
              </div>
            ) : null}

            {readerTab === 'quran' ? (
              <div role="tabpanel" className="books-reader-settings-panel">
                <div className="books-reader-settings-section">
                  <label className="books-reader-settings-label" htmlFor="wi-books-quran-translation">
                    Quran translation
                  </label>
                  <select
                    id="wi-books-quran-translation"
                    className="books-reader-select"
                    value={settings.quranTranslation}
                    onChange={(e) => setSettings((s) => ({ ...s, quranTranslation: e.target.value }))}
                  >
                    <option value="20">Saheeh International</option>
                    <option value="22">Yusuf Ali</option>
                    <option value="19">Pickthall</option>
                    <option value="85">Abdel Haleem</option>
                    <option value="84">T. Usmani</option>
                  </select>
                </div>
                <p className="books-reader-settings-hint">Translation applies to Quranic verses when available in this module.</p>
              </div>
            ) : null}

            {readerTab === 'reading' ? (
              <div role="tabpanel" className="books-reader-settings-panel">
                <p className="books-reader-settings-hint">
                  Modules use continuous reading. Use <strong>Save to study</strong>, <strong>Notes</strong>, and <strong>Chapters</strong> from
                  the bar above while you read.
                </p>
              </div>
            ) : null}

            <button type="button" className="books-reader-settings-done" onClick={() => setOpenPanel(null)}>
              Done
            </button>
          </div>
        </div>
      ) : null}

      {openPanel === 'note' ? (
        <div className="books-annotations-panel wi-books-note-panel" role="dialog" aria-modal="true" aria-label="Private note">
          <div className="books-annotations-header">
            <h3>Private note</h3>
            <button type="button" className="books-annotations-close" aria-label="Close" onClick={closePanels}>
              ×
            </button>
          </div>
          <div className="books-annotations-list wi-books-note-panel-body">{notePanel}</div>
        </div>
      ) : null}

      {openPanel === 'toc' || openPanel === 'more' ? (
        <button type="button" className="wi-books-module-panel-backdrop" aria-label="Close panel" onClick={closePanels} />
      ) : null}

      {openPanel === 'note' ? (
        <button type="button" className="wi-books-module-modal-backdrop" aria-label="Close notes" onClick={closePanels} />
      ) : null}

      {openPanel === 'more' ? (
        <div
          className="wi-books-more-flyout"
          role="dialog"
          aria-modal="true"
          aria-label="Deeper thoughts"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) closePanels();
          }}
        >
          <div className="wi-books-more-flyout-inner">{morePanel}</div>
        </div>
      ) : null}

      <div className="books-reader-scroll">
        <div className="books-reader-page-viewport">
          <article className={contentClass} style={contentStyle}>
            {children}
          </article>
        </div>
      </div>
    </div>
  );
}
